import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { IconAi, IconCheck, IconClients, IconClose, IconDownload, IconDuplicate, IconEdit, IconInvoices, IconPin, IconSend, IconStar, IconWarning } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { generateInvoicePDF } from '../lib/generateInvoicePDF';
import { generateQuoteOrderPDF } from '../lib/generateQuoteOrderPDF';
import EmptyState from '../components/EmptyState';
import { usePlan } from '../lib/PlanContext';

// ── ステータス＝書類種別を兼ねる2フェーズパイプライン ──
// 見積・注文フェーズ（書類＝見積書兼注文書）→ 請求フェーズ（書類＝請求書）
const STATUS_LIST = ['見積書作成', '見積書提出', '受注', '請求書作成', '請求書提出', '入金待ち', '入金済み', '失注'];

// 「→ 次へ進める」用の直線フロー（失注は分岐・終端なので含めない）
const ADVANCE_FLOW = ['見積書作成', '見積書提出', '受注', '請求書作成', '請求書提出', '入金待ち', '入金済み'];
const nextStatusOf = (status) => {
  const i = ADVANCE_FLOW.indexOf(status);
  return (i >= 0 && i < ADVANCE_FLOW.length - 1) ? ADVANCE_FLOW[i + 1] : null;
};
// 失注にできるのは受注前（見積中）のみ
const canLose = (status) => status === '見積書作成' || status === '見積書提出';

// ステータス → 書類種別（フェーズ）。受注も見積書兼注文書が担当
const PHASE_STATUSES = {
  quote:   ['見積書作成', '見積書提出', '受注', '失注'],
  invoice: ['請求書作成', '請求書提出', '入金待ち', '入金済み'],
};
const docTypeOf = (status) => PHASE_STATUSES.quote.includes(status) ? 'quote' : 'invoice';
const PHASE_ORDER = ['quote', 'invoice'];
const phaseRank = (status) => PHASE_ORDER.indexOf(docTypeOf(status));

const STATUS_STYLE = {
  '見積書作成':  { bg: '#1e3a3a', color: '#5eead4' },
  '見積書提出':  { bg: '#134e4a', color: '#2dd4bf' },
  '失注':     { bg: '#3f1d1d', color: '#fca5a5' },
  '受注':     { bg: '#422006', color: '#fcd34d' },
  '請求書作成':    { bg: '#334155', color: '#94a3b8' },
  '請求書提出':  { bg: '#1e3a5f', color: '#93c5fd' },
  '入金待ち':  { bg: '#451a03', color: '#fcd34d' },
  '入金済み':  { bg: '#14532d', color: '#86efac' },
};

// 書類種別ごとの表示メタ
const DOC_META = {
  quote:   { label: '見積書兼注文書', noField: 'estimate_no', btnBg: '#134e4a', btnColor: '#5eead4' },
  invoice: { label: '請求書',        noField: 'invoice_no',  btnBg: '#0f4c81', btnColor: '#93c5fd' },
};

// カードに出す「期日」はステータスで出し分け（見積・注文=有効期限 / 請求=支払期日）
const cardDateOf = (inv) => {
  if (docTypeOf(inv.status) === 'quote') return { label: '有効期限', value: inv.valid_until };
  return { label: '支払期日', value: inv.due_date };
};
// カードに出す「発行日」も現フェーズの書類のもの（見積・注文=見積発行日 / 請求=請求発行日）
const cardIssuedOf = (inv) =>
  docTypeOf(inv.status) === 'quote' ? (inv.estimate_issued_date || inv.issued_date) : inv.issued_date;

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '\u00a50';
  return '\u00a5' + Number(amount).toLocaleString('ja-JP');
};
const formatDate = (dateStr) => {
  if (!dateStr) return '未設定';
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
};

export default function Invoices() {
  const { theme } = useTheme();
  const { requirePro, isPro } = usePlan();   // 課金：見積書兼注文書PDFはProのみ
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('すべて');
  const [filterClient, setFilterClient] = useState('すべて');
  const [form, setForm] = useState({ title:'', amount:'', estimate_issued_date:'', issued_date:'', due_date:'', valid_until:'', status:'見積書作成', memo:'', client_id:'', tax_included:true, to_company:'', to_contact:'', payment_method:'銀行振込' });
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [useLinkedProjects, setUseLinkedProjects] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [profile, setProfile] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [quotePdfLoading, setQuotePdfLoading] = useState(null);
  const [pdfError, setPdfError] = useState('');
  const [mailModal, setMailModal] = useState(null); // 対象のinvoice
  const [mailTone, setMailTone] = useState('polite');
  const [mailResult, setMailResult] = useState(null);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailError, setMailError] = useState('');
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => { fetchInvoices(); fetchProfile(); fetchProjects(); fetchClients(); fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const { data } = await supabase.from('expenses').select('id, date, amount, category, memo').eq('user_id', user.id).order('date', { ascending: false });
      setExpenses(data || []);
    } catch(e) { console.error(e); }
  };

  const fetchClients = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const { data } = await supabase.from('clients').select('id, name').eq('user_id', user.id).order('name');
      setClients(data || []);
    } catch(e) { console.error(e); }
  };

  const fetchProfile = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const { data } = await supabase.from('profiles').select('name, bank_info, invoice_prefix, estimate_prefix, qualified_invoice_no, signature').eq('id', user.id).single();
      if (data) setProfile(data);
    } catch(e) { console.error(e); }
  };

  const fetchProjects = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const { data } = await supabase.from('projects').select('id, title, amount, status, due_date, memo, payment_method, work_type, work_days, hourly_rate, hours_worked, project_work_dates(id, date)').eq('user_id', user.id).order('created_at', { ascending: false });
      setProjects(data || []);
    } catch(e) { console.error(e); }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) { setLoading(false); return; }
      const { data, error } = await supabase
        .from('invoices').select('*, clients(id, name)').eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error) setInvoices(data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const openNew = () => {
    setEditInvoice(null);
    const today = new Date().toISOString().split('T')[0];
    const due = new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0];
    setForm({ title:'', amount:'', estimate_issued_date:today, issued_date:today, due_date:due, valid_until:due, status:'見積書作成', memo:'', client_id:'', tax_included:true, to_company:'', to_contact:'', payment_method:'銀行振込' });
    setSelectedProjects([]);
    setSelectedExpenses([]);
    setUseLinkedProjects(false);
    setShowModal(true);
  };

  const openEdit = (inv) => {
    setEditInvoice(inv);
    setForm({
      title:inv.title||'', amount:inv.amount||'',
      estimate_issued_date:inv.estimate_issued_date||'',
      issued_date:inv.issued_date||'', due_date:inv.due_date||'',
      valid_until:inv.valid_until||'',
      status:inv.status||'請求書作成', memo:inv.memo||'',
      client_id:inv.client_id||'',
      tax_included: inv.tax_included !== false, // デフォルトtrue
      to_company: inv.to_company||'',
      to_contact: inv.to_contact||'',
      payment_method: inv.payment_method || '銀行振込',
    });
    const linked = inv.linked_projects || [];
    setSelectedProjects(linked);
    setUseLinkedProjects(linked.length > 0);
    setSelectedExpenses(inv.linked_expenses || []);
    setShowModal(true);
  };

  // クライアント選択時に会社名・担当者を自動補完
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setForm(prev => ({
      ...prev,
      client_id: clientId,
      to_company: client ? client.name : prev.to_company,
      to_contact: client ? (client.contact_name || '') : prev.to_contact,
    }));
  };

  // 案件選択トグル
  const toggleProject = (proj) => {
    const exists = selectedProjects.find(p => p.id === proj.id);
    if (exists) {
      setSelectedProjects(selectedProjects.filter(p => p.id !== proj.id));
    } else {
      const total = calcProjectTotal(proj);
      const newList = [...selectedProjects, { id: proj.id, title: proj.title, amount: total, due_date: proj.due_date || '', memo: proj.memo || '' }];
      setSelectedProjects(newList);
      if (selectedProjects.length === 0 && proj.payment_method) {
        setForm(prev => ({ ...prev, payment_method: proj.payment_method }));
      }
    }
  };

  // 経費選択トグル
  const toggleExpense = (exp) => {
    const exists = selectedExpenses.find(e => e.id === exp.id);
    if (exists) {
      setSelectedExpenses(selectedExpenses.filter(e => e.id !== exp.id));
    } else {
      setSelectedExpenses([...selectedExpenses, { id: exp.id, category: exp.category, memo: exp.memo, amount: exp.amount || 0, date: exp.date }]);
    }
  };

  // work_typeを考慮した案件合計金額を計算
  const calcProjectTotal = (p) => {
    switch (p.work_type) {
      case 'daily':
        // 日当 × 稼働日数（project_work_datesの件数）
        return p.hourly_rate && (p.project_work_dates || []).length > 0
          ? p.hourly_rate * (p.project_work_dates || []).length
          : (p.amount || 0);
      case 'hourly':
        // 時給 × 稼働時間 × 稼働日数
        return p.hourly_rate && p.hours_worked && p.work_days
          ? p.hourly_rate * p.hours_worked * p.work_days
          : (p.amount || 0);
      default:
        // project / monthly はamountをそのまま
        return p.amount || 0;
    }
  };

  // 選択案件の合計金額
  const linkedTotal = selectedProjects.reduce((s, p) => s + (p.amount || 0), 0);
  const expensesTotal = selectedExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      // ── フェーズごとに独立採番（A方針）。既存番号は固定、現フェーズの番号だけ無ければ採番 ──
      const yr = new Date().getFullYear();
      const mkNo = (field, prefixField, def) => {
        if (editInvoice?.[field]) return editInvoice[field]; // 既存番号は変更しない
        const prefix = profile?.[prefixField] || def;
        const seq = invoices.filter(i => i[field]).length + 1;
        return `${prefix}-${yr}-${String(seq).padStart(3, '0')}`;
      };
      const phase = docTypeOf(form.status);
      const estimate_no = (phase === 'quote'   || editInvoice?.estimate_no) ? mkNo('estimate_no', 'estimate_prefix', 'EST') : (editInvoice?.estimate_no || null);
      const invoice_no  = (phase === 'invoice' || editInvoice?.invoice_no)  ? mkNo('invoice_no',  'invoice_prefix',  'INV') : (editInvoice?.invoice_no || null);

      // まとめ請求の場合は案件合計＋経費合計を金額にセット
      const finalAmount = useLinkedProjects && selectedProjects.length > 0
        ? linkedTotal + expensesTotal
        : (form.amount ? parseInt(form.amount) + expensesTotal : null);

      const payload = {
        title: form.title,
        amount: finalAmount,
        estimate_issued_date: form.estimate_issued_date || null,
        issued_date: form.issued_date || null,
        due_date: form.due_date || null,
        valid_until: form.valid_until || null,
        status: form.status,
        memo: form.memo,
        user_id: user.id,
        invoice_no,
        estimate_no,
        linked_projects: useLinkedProjects && selectedProjects.length > 0 ? selectedProjects : null,
        linked_expenses: selectedExpenses.length > 0 ? selectedExpenses : null,
        client_id: form.client_id || null,
        tax_included: form.tax_included,
        to_company: form.to_company || null,
        to_contact: form.to_contact || null,
        payment_method: form.payment_method || '銀行振込',
      };

      if (editInvoice) {
        await supabase.from('invoices').update(payload).eq('id', editInvoice.id);
      } else {
        await supabase.from('invoices').insert([payload]);
      }
      setShowModal(false);
      fetchInvoices();
    } catch(e) { console.error(e); }
  };

  const openMailModal = (inv) => {
    setMailModal(inv);
    setMailResult(null);
    setMailError('');
    setMailTone('polite');
  };

  const handleGenerateMail = async () => {
    if (!mailModal) return;
    setMailLoading(true);
    setMailError('');
    setMailResult(null);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const res = await fetch('/api/invoice-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-Id': userId } : {}) },
        body: JSON.stringify({
          client_name:  mailModal.clients?.name || mailModal.to_company || '',
          contact_name: mailModal.to_contact || '',
          project_name: mailModal.title || '',
          amount:       mailModal.amount || 0,
          due_date:     mailModal.due_date ? formatDate(mailModal.due_date) : '',
          bank_info:    profile?.bank_info || '',
          sender_name:  profile?.name || '',
          tone:         mailTone,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setMailError(data.message || data.error || 'エラーが発生しました'); return; }
      setMailResult(data);
    } catch (e) {
      setMailError('通信エラーが発生しました');
    } finally {
      setMailLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この取引を削除しますか？')) return;
    await supabase.from('invoices').delete().eq('id', id);
    fetchInvoices();
  };

  // その書類種別の番号が無ければ採番してDB保存・ローカル即時反映（A方針：種別ごと独立）
  const ensureDocNo = async (inv, docType) => {
    const field = DOC_META[docType].noField;
    if (inv[field]) return inv[field];
    const prefixField = docType === 'quote' ? 'estimate_prefix' : 'invoice_prefix';
    const def = docType === 'quote' ? 'EST' : 'INV';
    const prefix = profile?.[prefixField] || def;
    const seq = invoices.filter(i => i[field]).length + 1;
    const no = `${prefix}-${new Date().getFullYear()}-${String(seq).padStart(3, '0')}`;
    await supabase.from('invoices').update({ [field]: no }).eq('id', inv.id);
    setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, [field]: no } : i));
    return no;
  };

  // ステータスを進める＝フェーズ移行。新フェーズの番号を自動採番
  const updateStatus = async (inv, status) => {
    await supabase.from('invoices').update({ status }).eq('id', inv.id);
    try { await ensureDocNo({ ...inv, status }, docTypeOf(status)); } catch(e) { console.error('採番エラー:', e); }
    fetchInvoices();
  };

  const handleDownloadPDF = async (inv) => {
    setPdfLoading(inv.id);
    setPdfError('');
    try {
      const invoice_no = await ensureDocNo(inv, 'invoice');
      await generateInvoicePDF({ ...inv, invoice_no }, profile);
    } catch(e) {
      console.error('PDF generation error:', e);
      setPdfError('請求書PDFの生成に失敗しました。');
      setTimeout(() => setPdfError(''), 4000);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDownloadQuotePDF = async (inv) => {
    if (!requirePro('quoteOrderPdf')) return;   // 課金：Freeならアップグレードモーダル
    setQuotePdfLoading(inv.id);
    setPdfError('');
    try {
      const estimate_no = await ensureDocNo(inv, 'quote');
      await generateQuoteOrderPDF({ ...inv, estimate_no }, profile);
    } catch(e) {
      console.error('QuoteOrder PDF generation error:', e);
      setPdfError('見積書兼注文書PDFの生成に失敗しました。');
      setTimeout(() => setPdfError(''), 4000);
    } finally {
      setQuotePdfLoading(null);
    }
  };

  const filtered = invoices
    .filter(i => filterStatus === 'すべて' || i.status === filterStatus)
    .filter(i => filterClient === 'すべて' || i.client_id === filterClient);
  const totalPending = invoices.filter(i => i.status === '入金待ち' || i.status === '請求書提出').reduce((s,i) => s+(i.amount||0), 0);
  const totalPaid = invoices.filter(i => i.status === '入金済み').reduce((s,i) => s+(i.amount||0), 0);

  const s = {
    card: { background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:14, overflow:'hidden' },
    label: { fontSize:12, color:theme.textSecondary, marginBottom:6, display:'block' },
    input: { width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 14px', fontSize:14, color:theme.textPrimary, outline:'none', boxSizing:'border-box', colorScheme:'dark' },
  };

  return (
    <div style={{ padding:'20px 16px', maxWidth:680, margin:'0 auto' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconInvoices size={18}/> 取引管理</h1>
          <p style={{ fontSize:13, color:theme.textMuted, margin:'4px 0 0' }}>{invoices.length}件の取引</p>
        </div>
        <button onClick={openNew} style={{ background:theme.primary, color:theme.primaryText, border:'none', borderRadius:10, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          ＋ 新規作成
        </button>
      </div>

      {pdfError && (
        <div style={{ background:theme.dangerBg, border:'1px solid #7f1d1d', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:13, color:theme.dangerText }}>
          <IconWarning size={14}/> {pdfError}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
        <div style={{ ...s.card, borderTop:'2px solid #f59e0b', padding:'14px 16px' }}>
          <div style={{ fontSize:11, color:theme.textSecondary, marginBottom:6 }}>入金待ち</div>
          <div style={{ fontSize:20, fontWeight:700, color:theme.textPrimary }}>{formatCurrency(totalPending)}</div>
        </div>
        <div style={{ ...s.card, borderTop:'2px solid #22c55e', padding:'14px 16px' }}>
          <div style={{ fontSize:11, color:theme.textSecondary, marginBottom:6 }}>入金済み（累計）</div>
          <div style={{ fontSize:20, fontWeight:700, color:theme.textPrimary }}>{formatCurrency(totalPaid)}</div>
        </div>
      </div>

      <div style={{ display:'flex', gap:6, marginBottom:8, overflowX:'auto', padding:'4px 0 10px', WebkitOverflowScrolling:'touch' }}>
        {['すべて', ...STATUS_LIST].map(st => (
          <button key={st} onClick={() => setFilterStatus(st)} style={{
            flexShrink:0, padding:'7px 14px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', border:'none',
            background: filterStatus===st ? theme.accent : theme.bgInput,
            color: filterStatus===st ? '#fff' : '#94a3b8',
            outline: filterStatus===st ? 'none' : '1px solid #334155',
          }}>
            {st}{st!=='すべて' && ` ${invoices.filter(i=>i.status===st).length}`}
          </button>
        ))}
      </div>

      {clients.length > 0 && (
        <div style={{ display:'flex', gap:6, marginBottom:16, overflowX:'auto', padding:'4px 0 10px', WebkitOverflowScrolling:'touch' }}>
          {[{ id:'すべて', name:'すべて' }, ...clients].map(c => (
            <button key={c.id} onClick={() => setFilterClient(c.id)} style={{
              flexShrink:0, padding:'7px 14px', borderRadius:20, fontSize:11, fontWeight:500, cursor:'pointer', border:'none',
              background: filterClient===c.id ? '#0f4c81' : '#1e293b',
              color: filterClient===c.id ? '#93c5fd' : '#64748b',
              outline: filterClient===c.id ? 'none' : '1px solid #334155',
            }}>
              <IconClients size={12}/> {c.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:theme.textMuted }}>読み込み中...</div>
      ) : invoices.length === 0 ? (
        <EmptyState preset="invoices" onAction={openNew} />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:theme.textMuted, fontSize:14 }}>
          条件に合う取引がありません
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(inv => {
            const ss = STATUS_STYLE[inv.status] || STATUS_STYLE['請求書作成'];
            const isDownloading = pdfLoading === inv.id;
            const isQuoteDownloading = quotePdfLoading === inv.id;
            const hasLinked = inv.linked_projects?.length > 0;
            const dt = docTypeOf(inv.status);
            const rank = phaseRank(inv.status);
            const docNo = inv[DOC_META[dt].noField];
            const nextSt = nextStatusOf(inv.status);
            const cd = cardDateOf(inv);
            return (
              <div key={inv.id} style={s.card}>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                        <div style={{ fontSize:15, fontWeight:600, color:theme.textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.title || '無題'}</div>
                        {hasLinked && <span style={{ flexShrink:0, fontSize:10, padding:'2px 7px', borderRadius:10, background:theme.badgeBg9, color:theme.badgeTxt9 }}>まとめ {inv.linked_projects.length}件</span>}
                      </div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                        {inv.clients?.name && <span style={{ fontSize:11, background:theme.badgeBg7, color:theme.badgeTxt7, padding:'2px 8px', borderRadius:20 }}><IconClients size={12}/> {inv.clients.name}</span>}
                        <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:DOC_META[dt].btnBg, color:DOC_META[dt].btnColor }}>{DOC_META[dt].label}</span>
                        {docNo && <span style={{ fontSize:11, color:theme.textWeak }}>{docNo}</span>}
                      </div>
                    </div>
                    <span style={{ flexShrink:0, marginLeft:10, fontSize:12, padding:'3px 10px', borderRadius:20, background:ss.bg, color:ss.color, fontWeight:500 }}>
                      {inv.status}
                    </span>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                    {[
                      { label:'金額', value:formatCurrency(inv.amount) },
                      { label:'発行日', value:formatDate(cardIssuedOf(inv)) },
                      { label: cd.label, value:formatDate(cd.value) },
                    ].map(item => (
                      <div key={item.label} style={{ background:theme.bgInput, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:theme.textMuted, marginBottom:2 }}>{item.label}</div>
                        <div style={{ fontSize:12, fontWeight:600, color:theme.textPrimary }}>{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* フェーズ移行：次へ進める（＋見積中は失注） */}
                  <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
                    {nextSt && (
                      <button onClick={() => updateStatus(inv, nextSt)}
                        style={{ background:theme.accentBg, border:`1px solid ${theme.accent}`, borderRadius:8, padding:'5px 12px', fontSize:11, color:theme.accent, fontWeight:600, cursor:'pointer' }}>
                        → {nextSt}へ進める
                      </button>
                    )}
                    {canLose(inv.status) && (
                      <button onClick={() => updateStatus(inv, '失注')}
                        style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:8, padding:'5px 10px', fontSize:11, color:theme.textMuted, cursor:'pointer' }}>
                        → 失注
                      </button>
                    )}
                  </div>

                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button onClick={() => openEdit(inv)} style={{ flex:1, minWidth:80, background:theme.accentBg, color:theme.accent, border:`1px solid ${theme.accent}`, borderRadius:10, padding:'11px', fontSize:13, fontWeight:600, cursor:'pointer' }}><IconEdit size={14}/> 編集</button>

                    {/* 見積書兼注文書はどのフェーズでも出せる */}
                    <button onClick={() => handleDownloadQuotePDF(inv)} disabled={isQuoteDownloading}
                      style={{ background: isQuoteDownloading ? '#334155' : DOC_META.quote.btnBg, color: isQuoteDownloading ? '#64748b' : DOC_META.quote.btnColor, border:'none', borderRadius:8, padding:'7px 12px', fontSize:12, cursor: isQuoteDownloading ? 'default' : 'pointer', minWidth:120 }}>
                      <span style={{display:'flex',alignItems:'center',gap:5}}>
                        <IconDownload size={14}/>
                        {isQuoteDownloading ? '生成中...' : '見積書兼注文書PDF'}
                        {!isPro && <span style={{ fontSize:9, fontWeight:700, letterSpacing:'0.05em', padding:'1px 5px', borderRadius:999, background:'rgba(255,255,255,0.15)' }}>PRO</span>}
                      </span>
                    </button>

                    {/* 請求書・送付メールは請求フェーズに入ってから */}
                    {rank >= 1 && (
                      <button onClick={() => handleDownloadPDF(inv)} disabled={isDownloading}
                        style={{ background: isDownloading ? '#334155' : DOC_META.invoice.btnBg, color: isDownloading ? '#64748b' : DOC_META.invoice.btnColor, border:'none', borderRadius:8, padding:'7px 12px', fontSize:12, cursor: isDownloading ? 'default' : 'pointer', minWidth:90 }}>
                        <span style={{display:'flex',alignItems:'center',gap:5}}><IconDownload size={14}/>{isDownloading ? '生成中...' : '請求書PDF'}</span>
                      </button>
                    )}

                    {rank >= 1 && (
                      <button onClick={() => openMailModal(inv)} style={{ background:theme.badgeBg12, color:theme.badgeTxt6, border:'none', borderRadius:8, padding:'7px 12px', fontSize:12, cursor:'pointer' }}>
                        <span style={{display:'flex',alignItems:'center',gap:5}}><IconSend size={14}/> メールを生成する</span>
                      </button>
                    )}

                    <button onClick={() => handleDelete(inv.id)} style={{ background:theme.dangerBg, color:theme.dangerText, border:'none', borderRadius:8, padding:'7px 12px', fontSize:12, cursor:'pointer' }}>削除</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 新規作成・編集モーダル ── */}
      {showModal && (
        <div onClick={e => { if(e.target===e.currentTarget) setShowModal(false); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:50, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ background:theme.bgCard, width:'100%', maxWidth:520, borderRadius:'20px 20px 0 0', maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ position:'sticky', top:0, background:theme.bgCard, borderBottom:'1px solid #334155', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'20px 20px 0 0', zIndex:1 }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:theme.textPrimary, margin:0 }}>{editInvoice ? '取引を編集' : '新規取引を作成'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', color:theme.textMuted, fontSize:22, cursor:'pointer' }}><IconClose size={18}/></button>
            </div>

            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:14 }}>

              {/* ★ クライアント選択（自動補完付き） */}
              {clients.length > 0 && (
                <div>
                  <label style={s.label}>クライアント</label>
                  <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} style={s.input}>
                    <option value=''>未設定</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>選択すると請求先が自動入力されます</div>
                </div>
              )}

              {/* ★ 請求先会社名・担当者 */}
              <div style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ fontSize:12, color:theme.textSecondary, fontWeight:600 }}>請求先</div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>会社名</label>
                  <input type="text" placeholder="例：株式会社〇〇" value={form.to_company}
                    onChange={e => setForm({...form, to_company:e.target.value})} style={s.input} />
                </div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>担当者名</label>
                  <input type="text" placeholder="例：田中 様" value={form.to_contact}
                    onChange={e => setForm({...form, to_contact:e.target.value})} style={s.input} />
                </div>
              </div>

              {/* 件名 */}
              <div>
                <label style={s.label}>件名 *</label>
                <input type="text" placeholder="例：2026年3月分 制作費まとめ" value={form.title}
                  onChange={e => setForm({...form, title:e.target.value})} style={s.input} />
              </div>

              {/* ★ まとめ請求トグル */}
              <div>
                <label style={s.label}>請求タイプ</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[{ label:'通常請求', val:false }, { label:'案件まとめ請求', val:true }].map(opt => (
                    <button key={String(opt.val)} onClick={() => { setUseLinkedProjects(opt.val); setSelectedProjects([]); }}
                      style={{
                        flex:1, padding:'9px', borderRadius:10, border:'none', fontSize:13, fontWeight:600, cursor:'pointer',
                        background: useLinkedProjects === opt.val ? theme.accent : theme.bgInput,
                        color: useLinkedProjects === opt.val ? '#fff' : '#64748b',
                        outline: useLinkedProjects === opt.val ? 'none' : '1px solid #334155',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── 通常請求：金額入力 ── */}
              {!useLinkedProjects && (
                <div>
                  <label style={s.label}>金額（円）</label>
                  <input type="number" placeholder="100000" value={form.amount}
                    onChange={e => setForm({...form, amount:e.target.value})} style={s.input} />
                  {form.amount && <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{formatCurrency(form.amount)}</div>}
                </div>
              )}

              {/* ── まとめ請求：案件選択 ── */}
              {useLinkedProjects && (
                <div>
                  <label style={s.label}>対象案件を選択</label>
                  {projects.length === 0 ? (
                    <div style={{ background:theme.bgInput, borderRadius:10, padding:'14px', fontSize:13, color:theme.textMuted, textAlign:'center' }}>
                      案件がありません。先に案件を登録してください。
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
                      {projects.map(proj => {
                        const isSelected = selectedProjects.some(p => p.id === proj.id);
                        return (
                          <div key={proj.id} onClick={() => toggleProject(proj)}
                            style={{
                              background: isSelected ? '#1e2d5f' : '#0f172a',
                              border: `1px solid ${isSelected ? theme.accent : '#334155'}`,
                              borderRadius:10, padding:'10px 14px', cursor:'pointer',
                              display:'flex', justifyContent:'space-between', alignItems:'center',
                            }}>
                            <div>
                              <div style={{ fontSize:13, color: isSelected ? '#a5b4fc' : '#e2e8f0', fontWeight: isSelected ? 600 : 400 }}>{proj.title}</div>
                              <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>{proj.status}</div>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontSize:13, color: isSelected ? '#a5b4fc' : '#94a3b8', fontWeight:600 }}>{formatCurrency(calcProjectTotal(proj))}</span>
                              <span style={{ fontSize:16, color: isSelected ? theme.accent : '#334155' }}>{isSelected ? '✓' : '+'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedProjects.length > 0 && (
                    <div style={{ background:theme.bgInput, borderRadius:10, padding:'10px 14px', marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:12, color:theme.textMuted }}>{selectedProjects.length}件選択中</span>
                      <span style={{ fontSize:14, fontWeight:700, color:theme.accent }}>{formatCurrency(linkedTotal)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── 経費を追加（通常・まとめ共通） ── */}
              <div>
                <label style={s.label}>経費を追加（任意）</label>
                {expenses.length === 0 ? (
                  <div style={{ background:theme.bgInput, borderRadius:10, padding:'12px', fontSize:12, color:theme.textMuted, textAlign:'center' }}>経費が登録されていません</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:160, overflowY:'auto' }}>
                    {expenses.map(exp => {
                      const isSelected = selectedExpenses.some(e => e.id === exp.id);
                      return (
                        <div key={exp.id} onClick={() => toggleExpense(exp)}
                          style={{
                            background: isSelected ? '#1a2a1a' : '#0f172a',
                            border: `1px solid ${isSelected ? '#22c55e' : '#334155'}`,
                            borderRadius:10, padding:'10px 14px', cursor:'pointer',
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                          }}>
                          <div>
                            <div style={{ fontSize:12, color: isSelected ? '#86efac' : '#e2e8f0' }}>{exp.category}{exp.memo ? `　${exp.memo}` : ''}</div>
                            <div style={{ fontSize:11, color:theme.textMuted, marginTop:1 }}>{exp.date}</div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:13, color: isSelected ? '#86efac' : '#94a3b8', fontWeight:600 }}>{formatCurrency(exp.amount)}</span>
                            <span style={{ fontSize:16, color: isSelected ? '#22c55e' : '#334155' }}>{isSelected ? '✓' : '+'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedExpenses.length > 0 && (
                  <div style={{ background:theme.bgInput, borderRadius:10, padding:'10px 14px', marginTop:6, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:theme.textMuted }}>{selectedExpenses.length}件の経費</span>
                    <span style={{ fontSize:14, fontWeight:700, color:theme.colorGreen }}>+{formatCurrency(expensesTotal)}</span>
                  </div>
                )}
              </div>

              {/* ステータス（書類フェーズを決める） */}
              <div>
                <label style={s.label}>ステータス</label>
                <select value={form.status} onChange={e => setForm({...form, status:e.target.value})} style={s.input}>
                  {STATUS_LIST.map(st => <option key={st}>{st}</option>)}
                </select>
                <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>
                  {docTypeOf(form.status) === 'quote' ? '→ 見積書兼注文書として出力' : '→ 請求書として出力'}
                </div>
              </div>

              {/* 見積書 兼 注文書 の日付（見積書兼注文書PDFに反映） */}
              <div style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ fontSize:12, color:theme.textSecondary, fontWeight:600 }}>見積書 兼 注文書</div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>発行日</label>
                  <input type="date" value={form.estimate_issued_date}
                    onChange={e => setForm({...form, estimate_issued_date:e.target.value})} style={s.input} />
                </div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>有効期限</label>
                  <input type="date" value={form.valid_until}
                    onChange={e => setForm({...form, valid_until:e.target.value})} style={s.input} />
                </div>
              </div>

              {/* 請求書 の日付（請求書PDFに反映） */}
              <div style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ fontSize:12, color:theme.textSecondary, fontWeight:600 }}>請求書</div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>発行日</label>
                  <input type="date" value={form.issued_date}
                    onChange={e => setForm({...form, issued_date:e.target.value})} style={s.input} />
                </div>
                <div>
                  <label style={{ ...s.label, marginBottom:4 }}>支払期日</label>
                  <input type="date" value={form.due_date}
                    onChange={e => setForm({...form, due_date:e.target.value})} style={s.input} />
                </div>
              </div>

              <div>
                <label style={s.label}>メモ</label>
                <textarea rows={2} placeholder="備考..." value={form.memo}
                  onChange={e => setForm({...form, memo:e.target.value})}
                  style={{ ...s.input, resize:'vertical' }} />
              </div>

              {/* ★ 消費税有無 */}
              <div>
                <label style={s.label}>消費税</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[{ label:'課税（10%）', val:true }, { label:'非課税・免税', val:false }].map(opt => (
                    <button key={String(opt.val)} onClick={() => setForm({...form, tax_included:opt.val})}
                      style={{
                        flex:1, padding:'9px', borderRadius:10, border:'none', fontSize:13, fontWeight:600, cursor:'pointer',
                        background: form.tax_included === opt.val ? theme.accent : theme.bgInput,
                        color: form.tax_included === opt.val ? '#fff' : '#64748b',
                        outline: form.tax_included === opt.val ? 'none' : '1px solid #334155',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 支払い方法 */}
              <div>
                <label style={s.label}>支払い方法</label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {['現金','銀行振込','先払い','後払い','その他'].map(m => (
                    <button key={m} onClick={() => setForm({...form, payment_method:m})}
                      style={{ padding:'6px 12px', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                        background: form.payment_method === m ? '#0f2d4a' : '#0f172a',
                        color: form.payment_method === m ? '#7dd3fc' : '#64748b',
                        outline: form.payment_method === m ? '1px solid #3b82f6' : '1px solid #334155' }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display:'flex', gap:10, paddingBottom:8 }}>
                <button onClick={() => setShowModal(false)} style={{ flex:1, background:theme.badgeBg1, color:theme.textSecondary, border:'none', borderRadius:10, padding:'12px', fontSize:14, cursor:'pointer' }}>キャンセル</button>
                <button onClick={handleSave}
                  disabled={!form.title.trim() || (useLinkedProjects && selectedProjects.length === 0)}
                  style={{
                    flex:1, border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:600,
                    background: (form.title.trim() && (!useLinkedProjects || selectedProjects.length > 0)) ? theme.primary : theme.bgHover,
                    color: (form.title.trim() && (!useLinkedProjects || selectedProjects.length > 0)) ? '#fff' : '#64748b',
                    cursor: (form.title.trim() && (!useLinkedProjects || selectedProjects.length > 0)) ? 'pointer' : 'default',
                  }}>
                  {editInvoice ? '更新する' : '作成する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── メール生成モーダル ── */}
      {mailModal && (
        <div onClick={e => { if(e.target===e.currentTarget) setMailModal(null); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:60, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ background:theme.bgCard, width:'100%', maxWidth:540, borderRadius:'20px 20px 0 0', maxHeight:'92vh', overflowY:'auto' }}>

            {/* ヘッダー */}
            <div style={{ position:'sticky', top:0, background:theme.bgCard, borderBottom:'1px solid #334155', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'20px 20px 0 0', zIndex:1 }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconSend size={14}/> 送付メールを生成</h2>
              <button onClick={() => setMailModal(null)} style={{ background:'none', border:'none', color:theme.textMuted, fontSize:22, cursor:'pointer' }}><IconClose size={18}/></button>
            </div>

            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:16 }}>

              {/* 請求書サマリー */}
              <div style={{ background:theme.bgInput, borderRadius:10, padding:'12px 16px', fontSize:13 }}>
                <div style={{ color:theme.textSecondary, marginBottom:4 }}>{mailModal.title || '無題'}</div>
                <div style={{ color:theme.textPrimary, fontWeight:600, fontSize:15 }}>¥{Number(mailModal.amount||0).toLocaleString('ja-JP')}</div>
                {mailModal.clients?.name && <div style={{ color:theme.badgeTxt7, fontSize:12, marginTop:4 }}><IconClients size={12}/> {mailModal.clients.name}</div>}
              </div>

              {/* トーン選択 */}
              <div>
                <div style={{ fontSize:12, color:theme.textSecondary, marginBottom:8 }}>文体</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[{ val:'polite', label:'丁寧（ビジネス）', desc:'フォーマルな敬語' }, { val:'friendly', label:'親しみやすい', desc:'やわらかい文体' }].map(t => (
                    <button key={t.val} onClick={() => setMailTone(t.val)}
                      style={{ flex:1, padding:'10px', borderRadius:10, border:'none', cursor:'pointer', textAlign:'left',
                        background: mailTone===t.val ? '#312e81' : '#0f172a',
                        outline: mailTone===t.val ? `1px solid ${theme.accent}` : '1px solid #334155' }}>
                      <div style={{ fontSize:13, fontWeight:600, color: mailTone===t.val ? '#a5b4fc' : '#94a3b8' }}>{t.label}</div>
                      <div style={{ fontSize:11, color:theme.textWeak, marginTop:2 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 生成ボタン */}
              <button onClick={handleGenerateMail} disabled={mailLoading}
                style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', fontSize:14, fontWeight:600,
                  cursor: mailLoading ? 'default' : 'pointer',
                  background: mailLoading ? '#334155' : `linear-gradient(135deg, ${theme.accent}, ${theme.accentSub})`,
                  color: mailLoading ? '#64748b' : '#fff' }}>
                {mailLoading ? 'メールを生成中...' : <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}><IconSend size={14}/> メールを生成する</span>}
              </button>

              {mailError && (
                <div style={{ background:theme.dangerBg, border:'1px solid #7f1d1d', borderRadius:10, padding:'10px 14px', fontSize:13, color:theme.dangerText }}>{mailError}</div>
              )}

              {/* 生成結果 */}
              {mailResult && (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

                  {/* 件名 */}
                  <div style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:12, padding:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                      <span style={{ fontSize:11, color:theme.textMuted, fontWeight:600 }}>件名</span>
                      <button onClick={() => copyToClipboard(mailResult.subject, 'subject')}
                        style={{ background: copiedField==='subject' ? '#14532d' : '#1e293b', color: copiedField==='subject' ? '#86efac' : '#94a3b8', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer' }}>
                        {copiedField==='subject' ? 'コピー済み' : 'コピー'}
                      </button>
                    </div>
                    <div style={{ fontSize:14, color:theme.textPrimary, fontWeight:500 }}>{mailResult.subject}</div>
                  </div>

                  {/* 本文 */}
                  <div style={{ background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:12, padding:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                      <span style={{ fontSize:11, color:theme.textMuted, fontWeight:600 }}>本文</span>
                      <button onClick={() => copyToClipboard(mailResult.body.replace(/\\n/g, '\n'), 'body')}
                        style={{ background: copiedField==='body' ? '#14532d' : '#1e293b', color: copiedField==='body' ? '#86efac' : '#94a3b8', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer' }}>
                        {copiedField==='body' ? 'コピー済み' : 'コピー'}
                      </button>
                    </div>
                    <div style={{ fontSize:13, color:theme.colorLight, lineHeight:1.8, whiteSpace:'pre-wrap' }}>{mailResult.body.replace(/\\n/g, '\n')}</div>
                  </div>

                  {/* まとめてコピー */}
                  <button onClick={() => copyToClipboard(`件名: ${mailResult.subject}\n\n${mailResult.body.replace(/\\n/g, '\n')}`, 'all')}
                    style={{ width:'100%', padding:'11px', borderRadius:10, border:`1px solid ${theme.accent}`, background:'transparent', color:theme.badgeTxt9, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                    {copiedField==='all' ? 'コピーしました！' : '件名＋本文をまとめてコピー'}
                  </button>

                  {/* 注意点 */}
                  {mailResult.notes?.length > 0 && (
                    <div style={{ background:theme.badgeBg11, border:'1px solid #166534', borderRadius:10, padding:'12px 14px' }}>
                      <div style={{ fontSize:11, color:theme.badgeTxt6, fontWeight:600, marginBottom:6 }}><IconPin size={14}/> 送付時のポイント</div>
                      {mailResult.notes.map((n, i) => (
                        <div key={i} style={{ fontSize:12, color:theme.textSecondary, marginBottom:3 }}>• {n}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
